import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormSubmitHandler} from 'platform/components';
import {array, object} from 'yup';

import {useParams} from 'react-router-dom';

import {head, isNil} from 'ramda';
import {isUndefined} from 'ramda-adjunct';

import {
  useGetVehicleGroupQuery,
  usePatchVehicleGroupMutation,
  usePostVehicleGroupMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, yupString, useNavigate} from 'shared/web';

import {VehicleGroupForm} from './components/VehicleGroupForm';
import {VehicleGroup} from './types/VehicleGroup';

export function VehicleGroupsDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetVehicleGroupQuery(
    {vehicleGroupId: id ?? ''},
    {skip: !id}
  );
  const [postVehicleGroup] = usePostVehicleGroupMutation();
  const [patchVehicleGroup] = usePatchVehicleGroupMutation();

  const handleSubmit: FormSubmitHandler<VehicleGroup> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      ...data,
      name: data.name ?? '',
      vehicleMakes: isUndefined(data.vehicleMake) ? undefined : [data.vehicleMake],
    };

    if (isNil(id)) {
      return await postVehicleGroup({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.vehicleGroups);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchVehicleGroup({
      vehicleGroupId: id,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.vehicleGroups);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: VehicleGroup = {
    ...data,
    vehicleMake: head(data?.vehicleMakes ?? []),
  };

  const titleKey = isNil(id)
    ? 'entity.workshop.actions.newVehicleGroup'
    : 'entity.workshop.labels.vehicleGroups';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: data?.name ?? i18n.t('entity.workshop.actions.newVehicleGroup'),
          breadcrumbs: [
            {
              label: i18n.t('entity.workshop.labels.vehicleGroups'),
              href: settingsRoutes.vehicleGroups,
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        isCreating={isNil(id)}
        data-testid={testIds.settings.vehicleGroupsDetail('template')}
      >
        <Form<VehicleGroup>
          defaultValues={defaultValues}
          schema={FormSchema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <>
              <VehicleGroupForm control={control} formApi={formApi} />
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.vehicleGroups),
                  },
                  {
                    control,
                    type: 'form-button',
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                  },
                ]}
                data-testid={testIds.settings.vehicleGroupsDetail('footer')}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

const FormSchema = object({
  name: yupString.required(),
  vehicleType: yupString.nullable(),
  vehicleMake: yupString.nullable(),
  vehicleModel_families: array().of(yupString).nullable(),
  vehicleModels: array().of(yupString).nullable(),
});
